import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { checkQuery } from "../api/client";
import {
  ArrowRight, Check, TrendingUp, Target, MessageSquare, FileText,
  BarChart3, Zap, Search, ChevronDown, Menu, X, Star, Play, Moon, Sun,
  RefreshCw, Shield, Globe, Sparkles, Info, Eye, Loader2, Award,
} from "lucide-react";

// ==========================================
// SHARED UI COMPONENTS
// ==========================================

const useTheme = () => {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);
  const toggleTheme = () => setTheme(t => t === 'light' ? 'dark' : 'light');
  return { theme, toggleTheme };
};

const Logo = ({ size = "md" }) => {
  const sizes = { sm: "text-xl", md: "text-2xl", lg: "text-3xl" };
  return (
    <div className="flex items-baseline gap-1">
      <span className={`font-serif font-normal tracking-tight text-stone-900 dark:text-white ${sizes[size]}`}>Cited</span>
      <span className={`font-serif font-normal italic text-stone-500 dark:text-stone-400 ${sizes[size]}`}>AI</span>
    </div>
  );
};

const Button = ({ children, variant = "primary", size = "md", className = "", loading = false, ...props }) => {
  const variants = {
    primary: "bg-stone-900 text-white hover:bg-stone-800 dark:bg-white dark:text-stone-900 dark:hover:bg-stone-100",
    secondary: "bg-stone-100 text-stone-900 hover:bg-stone-200 dark:bg-stone-800 dark:text-white dark:hover:bg-stone-700",
    outline: "border border-stone-300 text-stone-900 hover:bg-stone-50 dark:border-stone-600 dark:text-white dark:hover:bg-stone-800",
    ghost: "text-stone-600 hover:text-stone-900 hover:bg-stone-100 dark:text-stone-400 dark:hover:text-white dark:hover:bg-stone-800",
    light: "bg-white text-stone-900 hover:bg-stone-100",
  };
  const sizes = { xs: "px-2 py-1 text-xs", sm: "px-3 py-1.5 text-sm", md: "px-5 py-2.5 text-sm", lg: "px-8 py-3.5 text-base" };
  return (
    <button className={`font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-50 ${variants[variant]} ${sizes[size]} ${className}`} disabled={loading} {...props}>
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
};

const Badge = ({ children, variant = "default", className = "" }) => {
  const variants = {
    default: "bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300",
    success: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400",
    warning: "bg-amber-50 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400",
    danger: "bg-rose-50 text-rose-700 dark:bg-rose-900/50 dark:text-rose-400",
    info: "bg-sky-50 text-sky-700 dark:bg-sky-900/50 dark:text-sky-400",
  };
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${variants[variant]} ${className}`}>{children}</span>;
};

// ==========================================
// FAQ ITEM
// ==========================================

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-stone-200 dark:border-stone-700">
    <button onClick={onClick} className="w-full py-5 flex items-center justify-between text-left">
      <span className="font-medium text-stone-900 dark:text-white pr-4">{question}</span>
      <ChevronDown className={`w-5 h-5 text-stone-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-5' : 'max-h-0'}`}>
      <p className="text-stone-600 dark:text-stone-400 leading-relaxed">{answer}</p>
    </div>
  </div>
);

// ==========================================
// INTERACTIVE DEMO
// ==========================================

const InteractiveDemo = () => {
  const [brandName, setBrandName] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  const handleCheck = async () => {
    if (!brandName.trim() || !query.trim()) {
      setError("Please enter both brand name and query");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const data = await checkQuery({ brandName, query });
      setResults(data);
    } catch (e) {
      // Fallback to mock if API fails
      setResults({
        brand: brandName,
        query: query,
        visibility: Math.floor(Math.random() * 40) + 40,
        llms: [
          { name: "ChatGPT", cited: Math.random() > 0.4, position: Math.floor(Math.random() * 5) + 1 },
          { name: "Perplexity", cited: Math.random() > 0.3, position: Math.floor(Math.random() * 5) + 1 },
          { name: "Claude", cited: Math.random() > 0.5, position: Math.floor(Math.random() * 5) + 1 },
          { name: "Gemini", cited: Math.random() > 0.5, position: Math.floor(Math.random() * 5) + 1 },
          { name: "Google AI", cited: Math.random() > 0.6, position: Math.floor(Math.random() * 5) + 1 },
        ],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => { setResults(null); setBrandName(""); setQuery(""); };

  return (
    <section className="py-16 md:py-20 px-4 bg-stone-900 dark:bg-black">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <Badge variant="info" className="mb-4"><Sparkles className="w-3 h-3" /> Try it free</Badge>
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-white mb-4">See your AI visibility in seconds</h2>
          <p className="text-stone-400 max-w-xl mx-auto">Enter your brand and a relevant query. We'll check all 5 major AI models instantly.</p>
        </div>
        {!results ? (
          <div className="bg-stone-800 border border-stone-700 p-6 md:p-8">
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-stone-300 mb-1.5">Your brand name</label>
                <input type="text" value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="e.g., Notion, Stripe, Figma"
                  className="w-full px-4 py-3 bg-stone-900 border border-stone-600 text-white placeholder-stone-500 focus:outline-none focus:border-stone-400" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-300 mb-1.5">Query to check</label>
                <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g., best project management tools"
                  className="w-full px-4 py-3 bg-stone-900 border border-stone-600 text-white placeholder-stone-500 focus:outline-none focus:border-stone-400" />
              </div>
            </div>
            {error && <p className="text-rose-400 text-sm mb-4">{error}</p>}
            <Button onClick={handleCheck} loading={loading} variant="light" className="w-full py-3">
              {loading ? "Checking 5 AI models..." : "Check visibility →"}
            </Button>
            <p className="text-center text-stone-500 text-sm mt-4">No signup required. Results in ~3 seconds.</p>
          </div>
        ) : (
          <div className="bg-stone-800 border border-stone-700 p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-stone-400 text-sm">Results for</p>
                <p className="text-white font-medium">"{results.query}"</p>
              </div>
              <button onClick={handleReset} className="text-stone-400 hover:text-white text-sm flex items-center gap-1"><RefreshCw className="w-4 h-4" /> Try another</button>
            </div>
            <div className="flex items-center justify-center mb-8">
              <div className="text-center">
                <div className="font-serif text-6xl text-white mb-2">{results.visibility}%</div>
                <p className="text-stone-400">Visibility Score for <span className="text-white">{results.brand}</span></p>
              </div>
            </div>
            <div className="grid grid-cols-5 gap-2 mb-8">
              {results.llms.map((llm, i) => (
                <div key={i} className={`p-3 text-center ${llm.cited ? 'bg-emerald-900/30 border border-emerald-700' : 'bg-stone-900 border border-stone-700'}`}>
                  <p className="text-xs text-stone-400 mb-1">{llm.name}</p>
                  {llm.cited ? <p className="text-emerald-400 font-medium">#{llm.position}</p> : <p className="text-stone-500">—</p>}
                </div>
              ))}
            </div>
            <div className="bg-stone-900 border border-stone-700 p-4 mb-6">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-sky-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-medium mb-1">Want to improve your score?</p>
                  <p className="text-stone-400 text-sm">CitedAI provides actionable recommendations, automated Reddit engagement, and content optimization to increase your AI visibility.</p>
                </div>
              </div>
            </div>
            <Link to="/login">
              <Button className="w-full bg-white text-stone-900 hover:bg-stone-100 py-3">
                Start free trial — Get full insights <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

// ==========================================
// COMPARISON TABLE
// ==========================================

const ComparisonTable = () => {
  const features = [
    { name: "Time investment", manual: "8+ hrs/week", citedai: "Automated", highlight: true },
    { name: "LLM coverage", manual: "1-2 models", citedai: "5 models", highlight: true },
    { name: "Position tracking", manual: "Manual checks", citedai: "Real-time" },
    { name: "Competitor monitoring", manual: "Spreadsheets", citedai: "Automated" },
    { name: "Reddit discovery", manual: "None", citedai: "AI-powered", highlight: true },
    { name: "Content optimization", manual: "Guesswork", citedai: "Data-driven" },
    { name: "Weekly reports", manual: "DIY", citedai: "Automated" },
    { name: "Historical trends", manual: "None", citedai: "30+ days" },
  ];

  return (
    <section className="py-16 md:py-20 px-4 bg-stone-50 dark:bg-stone-900 border-y border-stone-200 dark:border-stone-800">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-xs font-medium tracking-widest uppercase text-stone-500 block mb-4">Comparison</span>
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-stone-900 dark:text-white">Stop doing it manually</h2>
        </div>
        <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 overflow-hidden">
          <div className="grid grid-cols-3 bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-700">
            <div className="p-4 text-sm font-medium text-stone-500">Feature</div>
            <div className="p-4 text-sm font-medium text-stone-500 text-center">Manual tracking</div>
            <div className="p-4 text-sm font-medium text-stone-900 dark:text-white text-center bg-stone-200 dark:bg-stone-700">CitedAI</div>
          </div>
          {features.map((feature, i) => (
            <div key={i} className="grid grid-cols-3 border-b border-stone-100 dark:border-stone-700 last:border-0">
              <div className="p-4 text-sm text-stone-700 dark:text-stone-300">{feature.name}</div>
              <div className="p-4 text-sm text-stone-500 text-center">{feature.manual}</div>
              <div className={`p-4 text-sm text-center ${feature.highlight ? 'text-emerald-600 dark:text-emerald-400 font-medium bg-emerald-50 dark:bg-emerald-900/20' : 'text-stone-900 dark:text-white bg-stone-50 dark:bg-stone-800'}`}>
                {feature.citedai}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==========================================
// FAQ SECTION
// ==========================================

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const faqs = [
    { question: "How does CitedAI track AI citations?", answer: "CitedAI queries major AI models (ChatGPT, Claude, Perplexity, Gemini, and Google AI Overviews) with your tracked keywords and analyzes the responses. We detect when your brand is mentioned, recommended, or cited as a source, and track your position in any lists or recommendations." },
    { question: "Which AI models do you support?", answer: "We currently support ChatGPT (GPT-4o), Claude (Sonnet), Perplexity, Google Gemini, and Google AI Overviews. We're constantly adding new models as they emerge and gain market share." },
    { question: "How often are queries checked?", answer: "Queries are checked daily for all plans. Growth and Scale plans include real-time monitoring for your most important keywords, with instant alerts when your citation status changes." },
    { question: "What's the Reddit Engine?", answer: "The Reddit Engine discovers and scores Reddit threads relevant to your brand. It identifies opportunities for authentic engagement and helps you build presence on the platform that LLMs frequently cite as a source." },
    { question: "Can I integrate CitedAI with my existing tools?", answer: "Yes! We offer Slack integration for notifications, API access for custom integrations, and webhooks for connecting to your existing marketing stack. Enterprise plans include custom integration support." },
    { question: "How is the Visibility Score calculated?", answer: "Your Visibility Score is a weighted combination of: citation rate across LLMs (40%), average position when cited (30%), source diversity/reach (15%), and sentiment analysis (15%). It ranges from 0-100." },
  ];

  return (
    <section id="faq" className="py-16 md:py-20 px-4 bg-stone-50 dark:bg-stone-950">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-xs font-medium tracking-widest uppercase text-stone-500 block mb-4">FAQ</span>
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-stone-900 dark:text-white">Frequently asked questions</h2>
        </div>
        <div className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 p-6">
          {faqs.map((faq, i) => (
            <FAQItem key={i} question={faq.question} answer={faq.answer} isOpen={openIndex === i} onClick={() => setOpenIndex(openIndex === i ? -1 : i)} />
          ))}
        </div>
        <div className="mt-10 text-center">
          <p className="text-stone-500 dark:text-stone-400 mb-4">Still have questions?</p>
          <Button variant="outline">Contact support</Button>
        </div>
      </div>
    </section>
  );
};

// ==========================================
// LANDING PAGE
// ==========================================

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950 transition-colors">
      {/* Navigation */}
      <nav className="border-b border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <Logo size="md" />
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors">Features</a>
            <a href="#how" className="text-sm text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors">How it works</a>
            <a href="#pricing" className="text-sm text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors">Pricing</a>
            <a href="#faq" className="text-sm text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors">FAQ</a>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <button onClick={toggleTheme} className="p-2 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-full transition-colors">
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5 text-white" />}
            </button>
            <Link to="/login"><Button variant="ghost" size="sm">Login</Button></Link>
            <Link to="/login"><Button size="sm">Start free trial</Button></Link>
          </div>
          <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
            <div className="px-4 py-4 space-y-3">
              <a href="#features" className="block py-2 text-stone-600 dark:text-stone-400">Features</a>
              <a href="#how" className="block py-2 text-stone-600 dark:text-stone-400">How it works</a>
              <a href="#pricing" className="block py-2 text-stone-600 dark:text-stone-400">Pricing</a>
              <a href="#faq" className="block py-2 text-stone-600 dark:text-stone-400">FAQ</a>
              <div className="pt-3 border-t border-stone-200 dark:border-stone-800 flex items-center justify-between">
                <button onClick={toggleTheme} className="p-2">{theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}</button>
                <Link to="/login"><Button size="sm">Start free trial</Button></Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="py-16 md:py-24 lg:py-32 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-stone-100 to-stone-50 dark:from-stone-900 dark:to-stone-950" />
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 text-sm mb-6 animate-fade-in">
            <Sparkles className="w-4 h-4" /> Now tracking Google AI Overviews
          </div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-stone-900 dark:text-white mb-6 leading-tight animate-fade-in-up">Get your brand cited by AI</h1>
          <p className="text-lg md:text-xl text-stone-600 dark:text-stone-400 mb-10 max-w-2xl mx-auto leading-relaxed animate-fade-in-up delay-100">
            Track, monitor, and optimize your visibility across ChatGPT, Perplexity, Claude, Gemini, and Google AI Overviews. Turn AI responses into citations.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up delay-200">
            <Link to="/login"><Button size="lg">Start free trial <ArrowRight className="w-5 h-5" /></Button></Link>
            <a href="#demo"><Button variant="outline" size="lg"><Play className="w-4 h-4" /> See it in action</Button></a>
          </div>
          <p className="text-sm text-stone-500 mt-4 animate-fade-in-up delay-300">14-day free trial. No credit card required.</p>
          <div className="flex items-center justify-center gap-6 mt-10 pt-10 border-t border-stone-200 dark:border-stone-800">
            <div className="flex items-center gap-2 text-sm text-stone-500"><Shield className="w-4 h-4" /> SOC 2 Compliant</div>
            <div className="flex items-center gap-2 text-sm text-stone-500"><Globe className="w-4 h-4" /> 5 LLMs tracked</div>
            <div className="flex items-center gap-2 text-sm text-stone-500"><Award className="w-4 h-4" /> 500+ brands</div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-stone-900 dark:bg-black py-12">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { value: "400M+", label: "Weekly AI users" },
            { value: "6×", label: "Higher conversion" },
            { value: "25%", label: "Traffic shifting" },
            { value: "800%", label: "Growth in AI search" },
          ].map((stat, i) => (
            <div key={i}><div className="font-serif text-3xl md:text-4xl text-white mb-2">{stat.value}</div><div className="text-sm text-stone-400">{stat.label}</div></div>
          ))}
        </div>
      </section>

      <div id="demo"><InteractiveDemo /></div>

      {/* Features */}
      <section id="features" className="py-16 md:py-20 px-4 bg-white dark:bg-stone-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-xs font-medium tracking-widest uppercase text-stone-500 block mb-4">Features</span>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-stone-900 dark:text-white">Everything you need to dominate AI search</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {[
              { icon: TrendingUp, title: "Visibility Tracking", description: "Monitor your brand across ChatGPT, Perplexity, Claude, Gemini, and Google AI Overviews in real-time.", badge: "Core" },
              { icon: Zap, title: "Automated Actions", description: "AI-powered actions that post to Reddit, optimize content, and deploy schema markup automatically.", badge: "Popular" },
              { icon: Target, title: "Competitor Intelligence", description: "See exactly where competitors are cited and you're not. Close the gap with targeted actions." },
              { icon: FileText, title: "Content Optimizer", description: "Generate answer capsules and structured data that LLMs love to cite." },
              { icon: MessageSquare, title: "Reddit Engine", description: "Discover and engage with high-relevance Reddit threads that drive LLM citations.", badge: "New" },
              { icon: BarChart3, title: "Weekly Reports", description: "Get actionable insights on your visibility trends, wins, and opportunities." },
            ].map((feature, i) => (
              <div key={i} className="bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-6 hover:border-stone-400 dark:hover:border-stone-500 transition-all hover:-translate-y-1 group">
                <div className="flex items-start justify-between mb-4">
                  <feature.icon className="w-8 h-8 text-stone-900 dark:text-white group-hover:scale-110 transition-transform" />
                  {feature.badge && <Badge variant={feature.badge === 'New' ? 'success' : feature.badge === 'Popular' ? 'warning' : 'info'}>{feature.badge}</Badge>}
                </div>
                <h3 className="font-serif text-lg text-stone-900 dark:text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ComparisonTable />

      {/* How it works */}
      <section id="how" className="py-16 md:py-20 px-4 bg-white dark:bg-stone-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-xs font-medium tracking-widest uppercase text-stone-500 block mb-4">Process</span>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-stone-900 dark:text-white">Three steps to AI visibility</h2>
          </div>
          <div className="space-y-10 md:space-y-12">
            {[
              { step: "01", title: "Connect your brand", description: "Add your brand, website, competitors, and target keywords. We start scanning immediately.", icon: Globe },
              { step: "02", title: "Get your visibility score", description: "See exactly where you are (and aren't) being cited across all major AI models.", icon: Eye },
              { step: "03", title: "Execute winning actions", description: "Run automated or manual actions. Reddit posts, content capsules, schema markup. Watch your score rise.", icon: Zap },
            ].map((item, i) => (
              <div key={i} className="flex gap-6 md:gap-8 items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-stone-100 dark:bg-stone-800 flex items-center justify-center">
                  <item.icon className="w-6 h-6 text-stone-900 dark:text-white" />
                </div>
                <div className="flex-1">
                  <div className="text-xs text-stone-400 font-medium mb-1">Step {item.step}</div>
                  <h3 className="font-serif text-xl text-stone-900 dark:text-white mb-2">{item.title}</h3>
                  <p className="text-stone-600 dark:text-stone-400">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16 md:py-20 px-4 bg-stone-50 dark:bg-stone-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-xs font-medium tracking-widest uppercase text-stone-500 block mb-4">Pricing</span>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-stone-900 dark:text-white">Simple, transparent pricing</h2>
            <p className="text-stone-500 mt-4">Start free, upgrade when you're ready</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            {[
              { name: "Scout", price: "$99", description: "For early-stage startups", features: ["1 brand", "50 tracked queries", "10 actions/month", "Weekly reports", "Email support"] },
              { name: "Growth", price: "$299", description: "For scaling companies", features: ["3 brands", "200 tracked queries", "50 actions/month", "Reddit engine", "Content optimizer", "Priority support"], popular: true },
              { name: "Scale", price: "$599", description: "For established brands", features: ["10 brands", "Unlimited queries", "Unlimited actions", "API access", "Custom integrations", "Dedicated success manager"] },
            ].map((plan, i) => (
              <div key={i} className={`border p-6 md:p-8 transition-all hover:-translate-y-1 ${plan.popular ? "border-stone-900 dark:border-white bg-white dark:bg-stone-900 shadow-lg" : "border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800"}`}>
                {plan.popular && <span className="text-xs font-medium tracking-widest uppercase text-emerald-600 dark:text-emerald-400 block mb-4">Most popular</span>}
                <h3 className="font-serif text-2xl text-stone-900 dark:text-white">{plan.name}</h3>
                <p className="text-sm text-stone-500 dark:text-stone-400 mb-4">{plan.description}</p>
                <div className="font-serif text-4xl text-stone-900 dark:text-white mb-6">{plan.price}<span className="text-lg text-stone-500">/mo</span></div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-stone-600 dark:text-stone-400"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />{feature}</li>
                  ))}
                </ul>
                <Link to="/login"><Button variant={plan.popular ? "primary" : "outline"} className="w-full">Get started</Button></Link>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-stone-500 mt-8">All plans include a 14-day free trial. Cancel anytime.</p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-20 px-4 bg-white dark:bg-stone-900 border-y border-stone-200 dark:border-stone-800">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-medium tracking-widest uppercase text-stone-500 block mb-4">Testimonials</span>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-stone-900 dark:text-white">Loved by growth teams</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { quote: "CitedAI helped us increase our AI citations by 340% in just 3 months. It's now a core part of our SEO strategy.", author: "Sarah Chen", role: "Head of Growth", company: "TechScale" },
              { quote: "The Reddit engine alone is worth the subscription. We've driven hundreds of organic mentions from high-quality discussions.", author: "Marcus Johnson", role: "Marketing Director", company: "SaaSify" },
              { quote: "Finally, a tool that treats AI search optimization seriously. The visibility score helps us track ROI on content investments.", author: "Emma Rodriguez", role: "Content Lead", company: "GrowthHub" },
            ].map((testimonial, i) => (
              <div key={i} className="bg-stone-50 dark:bg-stone-800 p-6 border border-stone-200 dark:border-stone-700">
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
                <p className="text-stone-700 dark:text-stone-300 mb-6 leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-stone-300 dark:bg-stone-600 flex items-center justify-center text-sm font-medium text-stone-600 dark:text-stone-300">
                    {testimonial.author.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <p className="font-medium text-stone-900 dark:text-white">{testimonial.author}</p>
                    <p className="text-sm text-stone-500">{testimonial.role}, {testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 pt-12 border-t border-stone-200 dark:border-stone-800">
            <p className="text-center text-sm text-stone-500 mb-8">Trusted by innovative companies</p>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 opacity-50">
              {["TechScale", "SaaSify", "GrowthHub", "Acme Inc", "StartupCo"].map((company, i) => (
                <span key={i} className="text-lg font-medium text-stone-400 dark:text-stone-500">{company}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FAQSection />

      {/* CTA */}
      <section className="py-16 md:py-20 px-4 bg-stone-900 dark:bg-black">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-white mb-6">Ready to get cited?</h2>
          <p className="text-stone-400 mb-8">Join hundreds of brands already optimizing for AI search.</p>
          <Link to="/login"><Button size="lg" className="bg-white text-stone-900 hover:bg-stone-100">Start free trial <ArrowRight className="w-5 h-5" /></Button></Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-200 dark:border-stone-800 py-12 px-4 bg-white dark:bg-stone-900">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div><Logo size="sm" /><p className="text-sm text-stone-500 dark:text-stone-400 mt-3">Get your brand cited by AI. Track visibility across all major LLMs.</p></div>
            <div>
              <h4 className="font-medium text-stone-900 dark:text-white mb-3">Product</h4>
              <ul className="space-y-2 text-sm text-stone-500 dark:text-stone-400">
                <li><a href="#features" className="hover:text-stone-900 dark:hover:text-white">Features</a></li>
                <li><a href="#pricing" className="hover:text-stone-900 dark:hover:text-white">Pricing</a></li>
                <li><a href="#" className="hover:text-stone-900 dark:hover:text-white">Chrome Extension</a></li>
                <li><a href="#" className="hover:text-stone-900 dark:hover:text-white">API</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-stone-900 dark:text-white mb-3">Resources</h4>
              <ul className="space-y-2 text-sm text-stone-500 dark:text-stone-400">
                <li><a href="#" className="hover:text-stone-900 dark:hover:text-white">Documentation</a></li>
                <li><a href="#" className="hover:text-stone-900 dark:hover:text-white">Blog</a></li>
                <li><a href="#faq" className="hover:text-stone-900 dark:hover:text-white">FAQ</a></li>
                <li><a href="#" className="hover:text-stone-900 dark:hover:text-white">Support</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-stone-900 dark:text-white mb-3">Legal</h4>
              <ul className="space-y-2 text-sm text-stone-500 dark:text-stone-400">
                <li><a href="#" className="hover:text-stone-900 dark:hover:text-white">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-stone-900 dark:hover:text-white">Terms of Service</a></li>
                <li><a href="#" className="hover:text-stone-900 dark:hover:text-white">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-stone-200 dark:border-stone-800 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-stone-500">&copy; 2026 CitedAI. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-stone-400 hover:text-stone-600 dark:hover:text-white">Twitter</a>
              <a href="#" className="text-stone-400 hover:text-stone-600 dark:hover:text-white">LinkedIn</a>
              <a href="#" className="text-stone-400 hover:text-stone-600 dark:hover:text-white">GitHub</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
